import { useCallback } from 'react';

import { usePlaceIdDetails } from 'hooks/geolocation/use-place-id-details';
import { parsePlaceAddress } from 'utils/geolocation';
import { isNotNull } from 'utils/is-not-null';

import { getNearbyPlaces } from './utils';

export const usePostalCodeListAddresses = () => {
  const getDetails = usePlaceIdDetails();

  /**
   * Gets a list of predicted addresses based on the postal code which is part
   * of `placeId`.
   *
   * @param input - The input string as provided by the user.
   * @param placeId - The placeId of the postal code.
   * @returns {Promise<google.maps.places.AutocompletePrediction[]>}
   */
  const getAddressList = useCallback(
    async (
      input: string,
      placeId: string
    ): Promise<google.maps.places.AutocompletePrediction[]> => {
      const postalCodeDetails = await getDetails(placeId);
      const bounds = postalCodeDetails?.geometry.bounds;
      const zipCode =
        parsePlaceAddress(postalCodeDetails?.address_components, {
          useISOCountry: true,
          streetNameFirst: false,
        }).zip || input;

      const placesList = await getNearbyPlaces(bounds);

      const predictions = (placesList ?? [])
        .map(place => {
          const isAddressValid =
            place?.addressComponents &&
            place?.formattedAddress?.toLowerCase().includes(zipCode.toLowerCase()) &&
            addressComponentExists(place?.addressComponents, 'street_number');

          return isAddressValid ? place : null;
        })
        .filter(isNotNull)
        .map(toAutocompletePrediction);

      return predictions;
    },
    [getDetails]
  );

  return getAddressList;
};

/**
 * Returns whether the given component `type` exists in the address components.
 */
export function addressComponentExists(
  addressComponents: google.maps.places.AddressComponent[],
  type: string
): boolean {
  return Boolean(addressComponents.find(comp => comp.types.includes(type)));
}

/**
 * transform geocoderResult to AutocompletePrediction
 * @param place GeocoderResult
 * @returns {google.maps.places.AutocompletePrediction}
 */
export function toAutocompletePrediction(
  place: google.maps.places.Place
): google.maps.places.AutocompletePrediction {
  return {
    description: place.formattedAddress ?? '',
    place_id: place.id,
    structured_formatting: {
      main_text: place.formattedAddress ?? '',
      secondary_text: '',
      main_text_matched_substrings: [],
    },
    types: place.types ?? [],
    matched_substrings: [],
    terms: [],
  };
}
